import getConfig from 'next/config'
import { useEffect, useState, useContext } from 'react'

import { PATHS, L_STORAGE_KEYS } from '@src/consts'
import {
  useFetchUserMe,
  homePagePath,
  useCustomRouter,
  useLoginWithEmailAndPassword,
} from '@src/hooks'
import { TfaTokenContext } from '@src/pages/_app.page'
import { getUser, lStorage, toAuthenticationType } from '@src/utils'

import { STAGING_API_ENDPOINT } from './const'
import { UseSignin } from './type'

export const useSignin = ({
  setLoginErrors,
  getSigninFormValue,
}: UseSignin) => {
  const { router, isString } = useCustomRouter()
  const query = router.query
  const loginChallenge = isString(query.login_challenge)
    ? query.login_challenge
    : ''
  const { publicRuntimeConfig } = getConfig()
  const { API_ENDPOINT } = publicRuntimeConfig
  const tfaTokenContext = useContext(TfaTokenContext)

  // To be able to request the Staging API from Local to increase development efficiency
  const isConnectedLocalToStg =
    API_ENDPOINT === STAGING_API_ENDPOINT &&
    process.env.NODE_ENV !== 'production'

  const [enabledFetchUserMe, setEnabledFetchUserMe] = useState(false)

  const fetchUserMe = useFetchUserMe({ enabled: enabledFetchUserMe })

  const loginWithEmailAndPasswordMutation = useLoginWithEmailAndPassword()
  const isLoadingLoginWithEmailAndPassword =
    loginWithEmailAndPasswordMutation.isPending

  function handleLoginWithEmailAndPassword() {
    loginWithEmailAndPasswordMutation.mutate(
      {
        loginWithPasswordForm: {
          email: getSigninFormValue().email,
          password: getSigninFormValue().password,
        },
      },
      {
        onSuccess: async ({ data }) => {
          // To be able to request the Staging API from Local to increase development efficiency
          if (isConnectedLocalToStg && data.token) {
            lStorage.setItem({
              value: data.token,
              key: L_STORAGE_KEYS.TOKEN,
            })
          }
          if (data.tfaToken && data.tfaTokenExpiresAt) {
            tfaTokenContext?.setTfaToken(data.tfaToken)
            tfaTokenContext?.setTfaTokenExpiresAt(data.tfaTokenExpiresAt)
          }
          if (!data.twoFactorAuthRegisterStatus) {
            setEnabledFetchUserMe(true)
          } else if (data.twoFactorAuthRegisterStatus === 'APP') {
            lStorage.setItem({
              key: L_STORAGE_KEYS.MAIL_ADDRESS,
              value: data.email,
            })
            router.push({
              pathname: PATHS.SIGNIN_TFA_APP,
              query,
            })
          } else if (data.twoFactorAuthRegisterStatus === 'SMS') {
            lStorage.setItem({
              key: L_STORAGE_KEYS.SMS_PHONE_NUMBER,
              value: data.smsPhoneNumber,
            })
            lStorage.setItem({
              key: L_STORAGE_KEYS.MAIL_ADDRESS,
              value: data.email,
            })
            router.push({
              pathname: PATHS.SIGNIN_TFA_SMS,
              query,
            })
          }
        },
        onError: (error) => {
          if (error.data.errors.errors) {
            setLoginErrors(error.data.errors.errors)
          } else if (error.data.errors.password) {
            setLoginErrors(error.data.errors.password)
          } else {
            setLoginErrors(['不明なエラーが発生しました'])
          }
        },
      }
    )
  }

  function moveToNextUrl() {
    const user = getUser()
    const backUrl = query.backUrl as string
    if (!user) return

    //TODO: When SAML information can be retrieved by user-api, add a condition whether SAML is enforced or not.
    if (user.isTempPassword) {
      router.push({
        pathname: PATHS.REGISTER_PASSWORD,
      })
    } else if (backUrl) {
      window.location.replace(backUrl)
    } else if (loginChallenge) {
      router.push({
        pathname: PATHS.OAUTH_AUTHORIZE_ACCEPT,
        query: {
          login_challenge: loginChallenge,
          subject: user.id,
        },
      })
    } else {
      homePagePath(user)
    }
  }

  useEffect(() => {
    if (fetchUserMe.isSuccess) {
      const rawUserMe = fetchUserMe.data.data
      const convertedResponse = toAuthenticationType(rawUserMe)
      lStorage.setItem({ key: 'user', value: convertedResponse })
      moveToNextUrl()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUserMe.isSuccess])

  useEffect(() => {
    const user = getUser()
    // user logged in but not accepted oauth yet
    if (user?.id && !!loginChallenge) {
      router.push({
        pathname: PATHS.OAUTH_AUTHORIZE_ACCEPT,
        query: {
          login_challenge: loginChallenge,
          subject: user.id,
        },
      })
    }
  }, [loginChallenge, router])

  return {
    handleLoginWithEmailAndPassword,
    isLoadingLoginWithEmailAndPassword,
  }
}
